import React from 'react';

import { useModal } from 'context/ModalContext';
import { Heart, Home, Route, Search, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useRouteChangeHandler } from 'shared/hooks';
import { cn } from 'shared/lib';
import { getPathWithoutLang } from 'shared/lib/converter';
import { useInfoStore } from 'store/useInfoStore';

type TabItem = {
  labelKey: string;
  to: string;
  icon: React.ReactNode;
  /** Path prefixes that should mark this tab as active */
  activeMatchers: string[];
  /** Whether tap requires login (otherwise opens login modal) */
  requiresAuth?: boolean;
  /** GA `menu` 값 — i18n 라벨과 분리해 영문 고정 */
  gaLabel: string;
};

const ICON_SIZE = 22;

const TABS: TabItem[] = [
  {
    labelKey: 'navigation.home',
    to: '/',
    icon: <Home size={ICON_SIZE} />,
    activeMatchers: ['/'],
    gaLabel: 'Home',
  },
  {
    labelKey: 'navigation.roomsearch',
    to: '/listing',
    icon: <Search size={ICON_SIZE} />,
    activeMatchers: ['/listing'],
    gaLabel: 'Room Search',
  },
  {
    labelKey: 'navigation.services',
    to: '/all-services',
    icon: <Sparkles size={ICON_SIZE} />,
    activeMatchers: ['/all-services'],
    gaLabel: 'Services',
  },
  {
    labelKey: 'navigation.shortlist',
    to: '/shortlist',
    icon: <Heart size={ICON_SIZE} />,
    activeMatchers: ['/shortlist'],
    requiresAuth: true,
    gaLabel: 'Shortlist',
  },
  {
    // Journey 는 마이페이지 상위 화면이다 — 이름·마이페이지 메뉴·알림 진입을 모두 품고 있어
    // 탭 하나로 합친다. /my/* 는 이 탭을 거쳐 들어가므로 활성 표시를 함께 맞춘다.
    //
    // 로그인을 요구하지 않는다 — 비로그인은 잠긴 여정 미리보기와 가이드 글을 보게 되고,
    // 로그인 유도는 그 화면 안에서 한다. 모달로 막아서면 아직 계약이 없는 사람에게
    // Journey 가 무엇인지 보여줄 기회 자체가 없다.
    labelKey: 'navigation.journey',
    to: '/journey',
    icon: <Route size={ICON_SIZE} />,
    activeMatchers: ['/journey', '/my'],
    gaLabel: 'Journey',
  },
];

const BottomTabBar = () => {
  const { t } = useTranslation('common');
  const { handleRouteChange } = useRouteChangeHandler();
  const { pathname } = useLocation();
  const { accessToken } = useInfoStore();
  const { openModal, closeModal, MODAL_TYPES } = useModal();

  const isLogin = !!accessToken;

  // 비영어는 /ja/journey 처럼 언어가 앞에 붙는다(vi·zh·ja. 영어는 접두어가 없다).
  // 떼지 않고 비교하면 그 세 언어에서는 어느 탭에 있든 활성 표시가 켜지지 않는다.
  const path = getPathWithoutLang(pathname);

  const isActive = (matchers: string[]) => matchers.some((m) => (m === '/' ? path === '/' : path.startsWith(m)));

  const handleTabClick = (tab: TabItem) => {
    window.dataLayer?.push({
      event: 'click_menu',
      menu: tab.gaLabel,
      section: 'Bottom Tab',
    });

    if (tab.requiresAuth && !isLogin) {
      window.dataLayer?.push({
        event: 'view_login_modal',
        menu: tab.gaLabel,
        section: 'Bottom Tab',
      });
      openModal(MODAL_TYPES.LOGIN);
      return;
    }
    // 로그인 모달이 열려있는 상태에서 다른 탭(Home/Search/Services) 누르면 모달도 닫음
    closeModal();
    handleRouteChange(tab.to);
  };

  return (
    <nav
      // 탭바는 폰(<800px)에서만 — 태블릿 밴드(800~1279px, 모바일 레이아웃)는 햄버거 메뉴가 내비 담당
      className="fixed bottom-0 left-0 right-0 z-40 flex h-16 items-center justify-around border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom)] min-[800px]:hidden"
      aria-label="Bottom navigation"
    >
      {TABS.map((tab) => {
        const active = isActive(tab.activeMatchers);
        return (
          <button
            key={tab.to}
            type="button"
            onClick={() => handleTabClick(tab)}
            className={cn(
              'flex h-full flex-1 flex-col items-center justify-center gap-0.5 transition-colors',
              active ? 'text-primary' : 'text-gray-40 hover:text-gray-900',
            )}
            aria-label={t(tab.labelKey)}
          >
            {tab.icon}
            <span data-nosnippet className="text-[11px] font-medium leading-none">
              {t(tab.labelKey)}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default BottomTabBar;
